import type { CSSProperties } from 'react';
import type { ThemeTokens } from '@course-authoring/shared-types';
import { saveFeedbackColors } from '../../theme/saveFeedbackColors';
import {
  adminButton,
  adminField,
  adminFocusable,
  adminSurface,
  adminText,
  adminTokens as a,
} from '../../admin-ui';

type StyleValue = CSSProperties | ((...args: never[]) => CSSProperties);

/**
 * Theme Settings is an admin page that edits the course theme. The frame,
 * headings and controls use the admin recipes; the course examples it shows
 * (font preview, logo sample tiles, color swatches) stay on the shared course
 * tokens so they keep showing what learners will see.
 */
export function createThemeSettingsPageStyles(tokens: ThemeTokens, { narrow = false }: { narrow?: boolean } = {}) {
  const fontFamily = a.typography.fontFamily;
  const text = a.text.primary;
  // Course-example text color: the preview samples render as course content.
  const courseText = tokens.text.primary;

  return {
    page: {
      ...adminSurface.page,
      position: 'relative',
      flexShrink: 0,
      padding: 0,
      ...(narrow ? adminSurface.pageNarrow : {}),
      width: '100%',
      minHeight: '100%',
      fontFamily,
    },
    pageContent: { padding: `${tokens.spacing['3xl']} ${tokens.spacing.xl}` },
    dirtyControl: {
      backgroundColor: saveFeedbackColors.dirtyBackground,
      boxShadow: `inset 0 0 0 1px ${saveFeedbackColors.dirtyBorder}`,
      borderRadius: tokens.radius.sm,
    },
    pageInner: {
      maxWidth: '1264px',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.lg,
      border: 0,
      padding: 0,
      minWidth: 0,
    },

    card: {
      ...adminSurface.panel,
      padding: narrow ? a.spacing.lg : `${a.spacing.xl} ${a.spacing['2xl']} ${a.spacing['2xl']}`,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.xl,
      minWidth: 0,
    },
    cardTitle: {
      ...adminText.sectionTitle,
      fontSize: '20px',
    },
    cardSubtitle: {
      ...adminText.sectionTitle,
      fontSize: '16px',
    },

    bulletList: {
      margin: 0,
      paddingLeft: tokens.spacing.xl,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
      fontSize: a.typography.body.size,
      lineHeight: a.typography.body.lineHeight,
      color: text,
    },
    bulletItem: {},

    logoBody: {
      padding: tokens.spacing.md,
      margin: `-${tokens.spacing.md}`,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
    },
    logoDropZoneActive: {
      borderTopColor: tokens.brand.accent ?? tokens.brand.primary,
      borderRightColor: tokens.brand.accent ?? tokens.brand.primary,
      borderBottomColor: tokens.brand.accent ?? tokens.brand.primary,
      borderLeftColor: tokens.brand.accent ?? tokens.brand.primary,
      backgroundColor: tokens.choice.drag.bg,
    },
    dashedDropZone: {
      width: '388px',
      height: '165px',
      backgroundColor: 'transparent',
      borderTopWidth: '1px',
      borderRightWidth: '1px',
      borderBottomWidth: '1px',
      borderLeftWidth: '1px',
      borderTopStyle: 'dashed',
      borderRightStyle: 'dashed',
      borderBottomStyle: 'dashed',
      borderLeftStyle: 'dashed',
      borderTopColor: a.border.control,
      borderRightColor: a.border.control,
      borderBottomColor: a.border.control,
      borderLeftColor: a.border.control,
      borderRadius: a.radius.tile,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: a.spacing.md,
      boxSizing: 'border-box',
      maxWidth: '100%',
      cursor: 'pointer',
      ...adminFocusable,
      transition: `border-color ${tokens.ui.transitionDefault}, background-color ${tokens.ui.transitionDefault}`,
    },
    dashedDropZoneActive: {
      borderTopColor: a.action.primary,
      borderRightColor: a.action.primary,
      borderBottomColor: a.action.primary,
      borderLeftColor: a.action.primary,
      backgroundColor: a.surface.subtle,
    },
    dropZoneLabel: {
      fontSize: '16px',
      fontWeight: 700,
      color: text,
      textAlign: 'center',
    },
    dropZoneSubLabel: {
      fontSize: a.typography.label.size,
      color: a.text.secondary,
      textAlign: 'center',
    },
    dropZonePlusIcon: {
      width: '36px',
      height: '36px',
      borderRadius: a.radius.pill,
      backgroundColor: a.action.primary,
      color: a.text.onPrimary,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    dropZonePlusIconSm: {
      width: '24px',
      height: '24px',
      borderRadius: a.radius.pill,
      backgroundColor: a.action.primary,
      color: a.text.onPrimary,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    visuallyHiddenInput: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0,0,0,0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
    errorMessage: {
      margin: 0,
      fontSize: a.typography.body.size,
      lineHeight: a.typography.body.lineHeight,
      color: a.status.error.text,
    },

    uploadedLogoRow: {
      display: 'flex',
      gap: a.spacing.xl,
      flexWrap: 'wrap',
      alignItems: 'flex-start',
    },
    // The group carries the fixed width so it can shrink inside a narrow card.
    uploadedLogoGroup: {
      width: '388px',
      maxWidth: '100%',
      minWidth: 0,
    },
    uploadedLogoTile: {
      width: '100%',
      height: '165px',
      backgroundColor: 'transparent',
      border: `1px solid ${a.border.subtle}`,
      borderRadius: a.radius.tile,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: tokens.spacing.lg,
      boxSizing: 'border-box',
      position: 'relative',
    },
    uploadedLogoImage: {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    },
    uploadedLogoActions: {
      display: 'flex',
      gap: a.spacing.xl,
      marginTop: a.spacing.md,
      width: '100%',
      justifyContent: 'center',
    },
    uploadedLogoAction: (variant: 'replace' | 'delete') => ({
      ...adminFocusable,
      display: 'inline-flex',
      alignItems: 'center',
      gap: a.spacing.xs,
      background: 'transparent',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      fontFamily,
      fontSize: '15px',
      fontWeight: 700,
      color: variant === 'delete' ? a.status.error.text : a.text.primary,
    }),
    uploadedLogoActionIcon: {
      width: '20px',
      height: '20px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    uploadedSampleColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      width: '344px',
      maxWidth: '100%',
      minWidth: 0,
    },
    // Course example: keeps the course text color it used to inherit from the page.
    uploadedSampleTile: (background: string) => ({
      width: '344px',
      maxWidth: '100%',
      height: '131px',
      color: courseText,
      backgroundColor: background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: tokens.spacing.lg,
      boxSizing: 'border-box',
    }),
    uploadedSampleCaption: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.xs,
    },
    uploadedSampleCaptionTitle: {
      margin: 0,
      fontSize: a.typography.body.size,
      fontWeight: 700,
      lineHeight: a.typography.body.lineHeight,
      color: text,
    },
    uploadedSampleCaptionDetail: {
      margin: 0,
      fontSize: a.typography.label.size,
      lineHeight: a.typography.label.lineHeight,
      color: a.text.secondary,
    },
    certToggleLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: a.spacing.xs,
      fontSize: a.typography.label.size,
      lineHeight: a.typography.label.lineHeight,
      color: text,
      cursor: 'pointer',
      userSelect: 'none',
    },

    certSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.lg,
      marginTop: a.spacing.xl,
    },
    certSectionDivider: {
      border: 'none',
      borderTop: `1px solid ${a.border.subtle}`,
      margin: 0,
    },
    certSectionHeading: {
      ...adminText.sectionTitle,
      fontSize: '16px',
    },
    revertLink: {
      ...adminButton.link,
      alignSelf: 'flex-start',
      margin: 0,
      fontSize: a.typography.label.size,
      fontWeight: 400,
      color: a.text.secondary,
    },

    fontBody: {
      display: 'grid',
      gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'minmax(388px, 1fr) minmax(0, 1.6fr)',
      gap: narrow ? a.spacing.xl : a.spacing['2xl'],
      alignItems: 'start',
    },
    fontControls: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.lg,
    },
    fontSelectWrapper: { position: 'relative', width: '100%' },
    fontSelectChevron: { position: 'absolute', right: tokens.spacing.md, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: 12 },
    fontSelect: {
      ...adminField.select,
      width: '388px',
      maxWidth: '100%',
      height: '48px',
      padding: `0 ${a.spacing['2xl']} 0 ${a.spacing.md}`,
      fontFamily,
      appearance: 'none',
    },
    fontUploadButton: {
      ...adminFocusable,
      width: '243px',
      height: '52px',
      backgroundColor: a.surface.panel,
      borderTopWidth: '1px',
      borderRightWidth: '1px',
      borderBottomWidth: '1px',
      borderLeftWidth: '1px',
      borderTopStyle: 'dashed',
      borderRightStyle: 'dashed',
      borderBottomStyle: 'dashed',
      borderLeftStyle: 'dashed',
      borderTopColor: a.border.control,
      borderRightColor: a.border.control,
      borderBottomColor: a.border.control,
      borderLeftColor: a.border.control,
      borderRadius: a.radius.tile,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: a.spacing.md,
      padding: `0 ${a.spacing.lg}`,
      color: text,
      fontFamily,
      cursor: 'pointer',
      boxSizing: 'border-box',
    },
    fontUploadLabel: {
      fontSize: '15px',
      fontWeight: 700,
      color: text,
    },
    fontDisclaimer: {
      margin: 0,
      fontSize: a.typography.label.size,
      lineHeight: a.typography.label.lineHeight,
      color: a.text.secondary,
      maxWidth: '390px',
    },
    // Course example from here: the sample text keeps the course tokens so it
    // shows what learners will see. Only the neutral edge is admin framing.
    fontPreviewCard: {
      width: '100%',
      maxWidth: '612px',
      minHeight: '238px',
      backgroundColor: tokens.backgrounds.page,
      // Course sample: keeps the course text color instead of the admin frame's.
      color: courseText,
      // An outside ring, not a border: the sample keeps its exact content box.
      boxShadow: `0 0 0 1px ${a.border.subtle}`,
      borderRadius: tokens.radius.xs,
      padding: tokens.spacing.xl,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
      boxSizing: 'border-box',
    },
    fontPreviewH1: {
      margin: 0,
      fontSize: tokens.typography.sizes.h3,
      fontWeight: tokens.typography.h1.weight,
      lineHeight: tokens.typography.lineHeights.uiSoft,
      color: courseText,
    },
    fontPreviewH2: {
      margin: 0,
      fontSize: tokens.typography.sizes.lg,
      fontWeight: tokens.typography.h2.weight,
      lineHeight: tokens.typography.lineHeights.uiSoft,
      color: courseText,
    },
    fontPreviewBody: {
      margin: 0,
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.body.weight,
      lineHeight: tokens.typography.lineHeights.uiSoft,
      color: courseText,
    },
    fontPreviewCaption: {
      margin: 0,
      fontSize: tokens.typography.sizes.sm,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: tokens.typography.lineHeights.uiSoft,
      color: courseText,
    },

    colorsBody: {
      display: 'grid',
      gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '1fr 1fr',
      columnGap: a.spacing['2xl'],
      rowGap: a.spacing.xl,
      alignItems: 'start',
    },
    assistantHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
    },
    assistantDescription: {
      margin: 0,
      color: tokens.text.muted,
      fontSize: tokens.typography.sizes.base,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
    assistantBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xl,
      padding: tokens.spacing.md,
      margin: `-${tokens.spacing.md}`,
    },
    assistantAvatarRow: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: tokens.spacing.xl,
      flexWrap: 'wrap',
    },
    assistantAvatarGuidance: {
      flex: '1 1 300px',
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    assistantSubheading: {
      margin: 0,
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.h4.weight,
    },
    assistantAvatarControls: {
      width: '260px',
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    assistantAvatarTile: {
      minHeight: '134px',
      backgroundColor: tokens.backgrounds.page,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      boxSizing: 'border-box',
    },
    assistantAvatarImage: {
      width: '76px',
      height: '76px',
      borderRadius: tokens.radius.full,
      objectFit: 'cover',
    },
    assistantUploadButton: {
      width: '100%',
      minHeight: '134px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacing.sm,
      border: 'none',
      background: 'transparent',
      color: tokens.brand.accent ?? tokens.brand.primary,
      font: 'inherit',
      fontWeight: tokens.typography.h4.weight,
      cursor: 'pointer',
    },
    assistantUploadTitle: {
      fontWeight: tokens.typography.h4.weight,
    },
    assistantUploadSize: {
      fontSize: tokens.typography.sizes.sm,
      color: tokens.text.muted,
    },
    assistantUploadIcon: {
      width: '36px',
      height: '36px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: tokens.radius.full,
      backgroundColor: tokens.buttons.action.bg,
      color: tokens.text.onBrand,
    },
    assistantAvatarActions: {
      display: 'flex',
      justifyContent: 'center',
      gap: tokens.spacing.xl,
    },
    assistantFieldRow: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: tokens.spacing.xl,
      flexWrap: 'wrap',
    },
    assistantFieldsStack: {
      flex: '1 1 300px',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
    },
    assistantFieldColumn: {
      flex: '0 0 auto',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
    },
    assistantFieldLabelRow: {
      minHeight: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.sm,
    },
    assistantFieldLabel: {
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.h4.weight,
      color: text,
    },
    assistantRestoreLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: tokens.spacing.xs,
      fontSize: tokens.typography.sizes.sm,
      textDecoration: 'none',
    },
    assistantTextInput: {
      width: '100%',
      boxSizing: 'border-box',
      border: `1px solid ${tokens.borders.interactive}`,
      borderRadius: tokens.radius.xs,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      font: 'inherit',
      color: text,
      backgroundColor: tokens.backgrounds.surface,
    },
    assistantTextArea: {
      width: '100%',
      minHeight: '92px',
      boxSizing: 'border-box',
      resize: 'vertical',
      border: `1px solid ${tokens.borders.interactive}`,
      borderRadius: tokens.radius.xs,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      font: 'inherit',
      color: text,
      backgroundColor: tokens.backgrounds.surface,
    },
    assistantFieldHintRow: {
      minHeight: '20px',
      display: 'flex',
      justifyContent: 'space-between',
      gap: tokens.spacing.sm,
    },
    assistantFieldHelp: {
      color: tokens.text.muted,
      fontSize: tokens.typography.sizes.sm,
    },
    assistantCounter: {
      minHeight: '18px',
      alignSelf: 'flex-end',
      color: tokens.text.muted,
      fontSize: tokens.typography.sizes.sm,
      whiteSpace: 'nowrap',
    },
    assistantPreview: {
      flex: '0 1 340px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: tokens.spacing.md,
    },
    assistantPreviewLabel: {
      alignSelf: 'flex-start',
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.h4.weight,
    },
    assistantPreviewPanel: {
      width: '100%',
      borderRadius: tokens.radius.sm,
      overflow: 'hidden',
      backgroundColor: tokens.backgrounds.surface,
      boxShadow: tokens.shadow.sm,
    },
    assistantPreviewHeader: {
      minHeight: '44px',
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      padding: `0 ${tokens.spacing.lg}`,
      color: tokens.text.onBrand,
      fontWeight: tokens.typography.h4.weight,
    },
    assistantPreviewMessage: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      minHeight: '116px',
      boxSizing: 'border-box',
    },
    assistantPreviewAvatar: {
      width: '40px',
      height: '40px',
      flexShrink: 0,
      borderRadius: tokens.radius.full,
      objectFit: 'cover',
    },
    assistantPreviewCopy: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
      overflowWrap: 'anywhere',
      fontSize: tokens.typography.sizes.sm,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
    assistantPreviewFab: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      minHeight: '44px',
      whiteSpace: 'nowrap',
      border: 0,
      borderRadius: tokens.radius.sm,
      color: tokens.text.onBrand,
      font: 'inherit',
      fontWeight: tokens.typography.weights.bold,
      boxShadow: tokens.shadow.md,
    },

    colorsColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
    },
    colorsColumnTitle: {
      margin: 0,
      fontSize: tokens.typography.sizes.ui,
      fontWeight: tokens.typography.h3.weight,
      lineHeight: tokens.typography.lineHeights.uiSoft,
      color: text,
    },
    colorRow: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.sm,
      maxWidth: '388px',
    },
    colorRowLabel: {
      fontSize: a.typography.body.size,
      fontWeight: 600,
      lineHeight: a.typography.body.lineHeight,
      color: text,
    },
    colorRowLabelDetail: {
      display: 'block',
      marginTop: '2px',
      fontSize: a.typography.label.size,
      fontWeight: 400,
      lineHeight: a.typography.label.lineHeight,
      color: a.text.secondary,
    },
    colorInput: {
      width: '100%',
      height: '48px',
      backgroundColor: a.surface.panel,
      border: `1px solid ${a.border.control}`,
      borderRadius: a.radius.control,
      padding: `${a.spacing.xs} ${a.spacing.sm} ${a.spacing.xs} ${a.spacing.md}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: a.spacing.md,
      boxSizing: 'border-box',
      cursor: 'pointer',
    },
    colorInputLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.md,
      minWidth: 0,
    },
    colorSwatch: (background: string) => ({
      width: '24px',
      height: '24px',
      borderRadius: tokens.radius.full,
      backgroundColor: background,
      border: `1px solid ${tokens.borders.default}`,
      flexShrink: 0,
      boxSizing: 'border-box',
    }),
    colorHexText: {
      fontSize: a.typography.body.size,
      fontWeight: 600,
      color: text,
      letterSpacing: '0.01em',
    },
    colorEditIcon: {
      position: 'relative',
      width: '36px',
      height: '36px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: text,
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      flexShrink: 0,
    },
    hiddenColorInput: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      opacity: 0,
      cursor: 'pointer',
      padding: 0,
      border: 'none',
      background: 'transparent',
    },
  } satisfies Record<string, StyleValue>;
}

export type ThemeSettingsPageStyles = ReturnType<typeof createThemeSettingsPageStyles>;
